import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  HLM_DIALOG_SHELL,
  HlmBtnDirective,
  HlmCheckboxComponent,
  HlmIconComponent,
} from '../../ui';

export interface RemoveTagConfirmData {
  tagName: string;
  number: string;
}

export interface RemoveTagConfirmResult {
  proceed: boolean;
  remember: boolean;
}

/**
 * Confirm modal for removing a number tag. Tags are visible to the customer, so removal is a
 * deliberate, confirmed action — but power users editing many tags can opt out for the session
 * via the checkbox (mirrors the large-range search opt-out).
 */
@Component({
  selector: 'cmt-remove-tag-confirm-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HLM_DIALOG_SHELL, HlmBtnDirective, HlmIconComponent, HlmCheckboxComponent],
  template: `
    <hlm-dialog-surface size="sm">
      <hlm-dialog-header>
        <hlm-dialog-icon-badge tone="destructive">
          <hlm-icon name="lucideTriangleAlert" size="1.25rem" />
        </hlm-dialog-icon-badge>
        <div hlmDialogHeading>
          <h2 hlmDialogTitle>Remove Tag</h2>
          <p hlmDialogDescription>
            Remove tag <strong>"{{ data.tagName }}"</strong> from {{ data.number }}?
            This changes customer visible data.
          </p>
        </div>
        <button hlmDialogClose tabindex="-1" (click)="cancel()">
          <hlm-icon name="lucideX" size="1rem" />
        </button>
      </hlm-dialog-header>

      <section hlmDialogBody>
        <label class="flex items-center gap-2 text-base text-foreground">
          <hlm-checkbox [checked]="rememberChoice()" (checkedChange)="rememberChoice.set($event)" />
          <span>Don't ask again for this session</span>
        </label>
      </section>

      <footer hlmDialogFooter>
        <button hlmBtn variant="outline" type="button" (click)="cancel()">Cancel</button>
        <button hlmBtn variant="destructive" type="button" (click)="confirm()">
          <hlm-icon name="lucideTrash2" size="0.9rem" />
          Remove
        </button>
      </footer>
    </hlm-dialog-surface>
  `,
})
export class RemoveTagConfirmDialogComponent {
  readonly data = inject<RemoveTagConfirmData>(MAT_DIALOG_DATA);
  private readonly ref = inject<MatDialogRef<RemoveTagConfirmDialogComponent, RemoveTagConfirmResult>>(MatDialogRef);

  readonly rememberChoice = signal(false);

  cancel(): void {
    this.ref.close({ proceed: false, remember: false });
  }
  confirm(): void {
    this.ref.close({ proceed: true, remember: this.rememberChoice() });
  }
}
